import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { API } from 'aws-amplify';
import { addDays, differenceInHours, parse } from 'date-fns';
import { meanBy, minBy } from 'lodash';
import React from 'react';
import { Link } from 'react-router-dom';
import { AppContext } from '../../../../contexts/app-context';
import { AuthContext } from '../../../../contexts/auth-context';
import { uicolors } from '../../../../theme';
import { shareContent } from '../../../../utils/share-content';
import { CourseProgressRing } from './course-progress-ring';
import { ProgressBar } from './progress-bar';

/**
 * Calculates days and hours remaining in a countdown from a start date.
 * Defaults to a 30-day window. Returns zeroes once the countdown has elapsed.
 */
const getCountdownRemaining = (
  startDate: Date | string,
  totalDays: number = 30,
): { days: number; hours: number } => {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const endDate = addDays(start, totalDays);
  const hoursLeft = differenceInHours(endDate, new Date());

  if (hoursLeft <= 0) {
    return { days: 0, hours: 0 };
  }

  return {
    days: Math.floor(hoursLeft / 24),
    hours: hoursLeft % 24,
  };
};

interface AgentEnrolledData {
  DateEnrolled: string | null;
  PreLicensingCourse: string | null;
  PreLicensingCourseProgress: number | null;
  PreLicensingCourseCompletedDate: string | null;
}

interface RaceProgressData {
  AgtNo: string | null;
  DirectDownlineCount: number | null;
  XcelAgtCount: number | null;
  HasEventTicket: boolean | null;
  EnrolledIn: AgentEnrolledData[];
}

export function AgentRace() {
  // Context
  const {
    state: { impersonatedAgent },
  } = React.useContext(AppContext);
  const {
    state: { user },
  } = React.useContext(AuthContext);
  // Hooks
  const queryClient = useQueryClient();

  const AgtNo = impersonatedAgent?.agentno || user.username;

  // Query - Agent Photo
  const pathPhoto = '/profile/photo';
  const queryPhotoKey = [pathPhoto, { Agentno: AgtNo }];
  const queryPhoto = useQuery({
    queryKey: queryPhotoKey,
    queryFn: async () => {
      const response: {
        data: string;
      } = await API.post('DashboardAPI', pathPhoto, {
        body: { Agentno: AgtNo },
      });

      return response.data;
    },
  });

  // Query - Agent Progress
  const pathProgress = '/homepage/xcel';
  const queryProgress = useQuery({
    queryKey: [pathProgress, { AgentNo: AgtNo }],
    queryFn: async () => {
      const response: {
        data: RaceProgressData | null;
      } = await API.post('DashboardAPI', pathProgress, {
        body: { AgentNo: AgtNo },
      });

      return response.data || null;
    },
  });

  const isLoading = queryPhoto.isLoading || queryProgress.isLoading;
  const isError = queryPhoto.isError || queryProgress.isError;

  // Get Progress for Course Ring around Avatar
  const averageProgress =
    meanBy(
      queryProgress.data?.EnrolledIn.filter(
        (item) => item.PreLicensingCourseProgress !== null,
      ),
      (item) => item.PreLicensingCourseProgress!,
    ) || 0;

  // Get the oldest DateEnrolled for 30 Day Race Start
  const parseEnrolled = (d: string) =>
    parse(d, 'MM/dd/yyyy HH:mm:ss', new Date());
  const oldestEnrolled = minBy(
    queryProgress.data?.EnrolledIn.filter((item) => item.DateEnrolled !== null),
    (item) => parseEnrolled(item.DateEnrolled!).getTime(),
  );
  // Days and hours remaining in countdown
  const remaining = oldestEnrolled?.DateEnrolled
    ? getCountdownRemaining(new Date(oldestEnrolled.DateEnrolled).toISOString())
    : null;
  const timeExpired: boolean =
    (remaining && remaining.days === 0 && remaining.hours === 0) || false;

  return (
    <Box
      sx={{
        flex: 1,
        borderRadius: 2,
        border: '1px solid',
        borderColor: uicolors.black[600],
        color: uicolors.black[50],
        position: 'relative',
      }}
    >
      {isLoading ? (
        <CircularProgress
          size={24}
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            color: uicolors.black[400],
          }}
        />
      ) : isError ? (
        <Tooltip title="Error fetching data" placement="right" arrow>
          <ErrorOutlineOutlinedIcon
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              color: uicolors.red[400],
            }}
          />
        </Tooltip>
      ) : null}

      <Stack spacing={2} sx={{ height: '100%', p: 1 }}>
        <Stack alignItems="center" justifyContent="center">
          <Box
            sx={{
              fontSize: 28,
              fontFamily: 'Racing Sans One',
              textAlign: 'center',
            }}
          >
            {timeExpired ? 'Grow your Business' : '30 Day Race'}
          </Box>
          {remaining || isLoading ? null : (
            <Box
              sx={{
                color: uicolors.yellow[400],
                fontWeight: 'bold',
                fontSize: 12,
                textTransform: 'uppercase',
              }}
            >
              Enroll to Begin Race
            </Box>
          )}
        </Stack>

        <Box sx={{ flex: 1 }}>
          <Stack
            spacing={1}
            direction={{ xs: 'column', md: 'row' }}
            alignItems="center"
            sx={{ height: '100%' }}
          >
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CourseProgressRing
                  value={averageProgress}
                  size={168}
                  avatarSrc={queryPhoto.data}
                  onUploadSuccess={() =>
                    queryClient.invalidateQueries({ queryKey: queryPhotoKey })
                  }
                />
              </Box>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Stack spacing={1}>
                <Box>
                  {timeExpired ? (
                    <Box sx={{ fontSize: 18, fontWeight: 'bold' }}>
                      Build your team when you
                    </Box>
                  ) : (
                    <Box sx={{ fontSize: 18 }}>
                      Earn{' '}
                      <Box
                        component="span"
                        sx={{
                          fontFamily: 'Racing Sans One',
                          fontSize: 22,
                          color: uicolors.green[500],
                        }}
                      >
                        $50
                      </Box>{' '}
                      in{' '}
                      <Box
                        component="span"
                        sx={{
                          fontFamily: 'Roboto Mono',
                          fontWeight: 'bold',
                          fontSize: 18,
                        }}
                      >
                        ARC
                      </Box>{' '}
                      when you
                    </Box>
                  )}
                  <Box component="ul" sx={{ m: 0, fontStyle: 'italic' }}>
                    <Box component="li">
                      <Box
                        component="button"
                        onClick={async () => {
                          const url =
                            `${
                              process.env.REACT_APP_AWS_BRANCH === 'master'
                                ? 'https://thealliancecontracting.com'
                                : 'https://rsnider.dfqh0r9fd4ua8.amplifyapp.com'
                            }/sign-up/the-alliance?RecAgt=` + user.username;

                          await shareContent({
                            content: {
                              title: 'Alliance Registration Link',
                              text: 'Get your NAA number below to unlock access to our training, tools, and resources.',
                              url,
                            },
                          });
                        }}
                        sx={{
                          p: 0,
                          border: 'none',
                          background: 'none',
                          fontSize: 16,
                          fontFamily: 'Roboto',
                          fontStyle: 'italic',
                          textAlign: 'left',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          color: uicolors.black[50],
                          ':hover': {
                            color: uicolors.green[400],
                          },
                        }}
                      >
                        <Stack spacing={1} direction="row" alignItems="center">
                          <Box>Fastract 3</Box>

                          <Box
                            sx={{
                              borderRadius: 50,
                              border: '1px solid',
                              borderColor: uicolors.green[800],
                              backgroundColor: uicolors.black[700],
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: 22,
                              width: 22,
                              transform: 'rotate(-15deg)',
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M4 11v2h16v-2zm12 2v2h2v-2zm-2 2v2h2v-2zm-2 2v2h2v-2zm4-6V9h2v2z" />
                              <path d="M14 15V7h2v8zm-2 2V5h2v12z" />
                            </svg>
                          </Box>
                        </Stack>
                      </Box>
                    </Box>

                    <Box component="li">
                      <Box
                        component={Link}
                        to="/resources/pre-licensing"
                        sx={{
                          textDecorationLine: 'none',
                          transition: 'all 0.2s',
                          color: uicolors.black[50],
                          ':hover': {
                            color: uicolors.green[400],
                          },
                        }}
                      >
                        <Stack spacing={1} direction="row" alignItems="center">
                          <Box>Enroll 3 in Xcel</Box>

                          <Box
                            sx={{
                              borderRadius: 50,
                              border: '1px solid',
                              borderColor: uicolors.green[800],
                              backgroundColor: uicolors.black[700],
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: 22,
                              width: 22,
                              transform: 'rotate(-15deg)',
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M4 11v2h16v-2zm12 2v2h2v-2zm-2 2v2h2v-2zm-2 2v2h2v-2zm4-6V9h2v2z" />
                              <path d="M14 15V7h2v8zm-2 2V5h2v12z" />
                            </svg>
                          </Box>
                        </Stack>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {timeExpired ? (
                  <Box sx={{ pt: 1 }}>
                    <Box sx={{ fontSize: 18, fontWeight: 'bold' }}>
                      Acquire new skills at
                    </Box>
                    <Box
                      component="a"
                      href="https://naauniversity.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: 'aqua',
                        textDecorationLine: 'none',
                        ':hover': {
                          textDecorationLine: 'underline',
                        },
                      }}
                    >
                      The Alliance University
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Stack spacing={1} direction="row" alignItems="center">
                      <Box sx={{ fontFamily: 'Racing Sans One', fontSize: 24 }}>
                        Countdown
                      </Box>

                      <Box
                        sx={{
                          flex: 1,
                          border: '1px dashed',
                          borderColor: uicolors.black[600],
                        }}
                      />

                      <Box sx={{ flex: 1 }} />
                    </Stack>

                    {remaining ? (
                      <Box sx={{ fontSize: 24 }}>
                        <Box
                          component="span"
                          sx={{ fontFamily: 'Roboto Mono', fontWeight: 'bold' }}
                        >
                          {remaining.days}
                        </Box>{' '}
                        Days{' '}
                        <Box
                          component="span"
                          sx={{ fontFamily: 'Roboto Mono', fontWeight: 'bold' }}
                        >
                          {remaining.hours}
                        </Box>{' '}
                        Hours
                      </Box>
                    ) : (
                      <Box sx={{ fontSize: 24, fontFamily: 'Roboto Mono' }}>
                        ---
                      </Box>
                    )}
                  </Box>
                )}
              </Stack>
            </Box>
          </Stack>
        </Box>

        <Stack
          spacing={1}
          direction={{ xs: 'column', md: 'row' }}
          alignItems="center"
        >
          {[
            {
              text: 'Agent Photo',
              completed: Boolean(queryPhoto.data),
              href: 'https://thealliancecontracting.com/account',
              to: '',
            },
            {
              text: 'Enrolled in Xcel',
              completed: Boolean(remaining),
              href: '',
              to: '/resources/pre-licensing',
            },
            {
              text: 'Event Ticket',
              completed: Boolean(queryProgress.data?.HasEventTicket),
              href: 'https://theallianceevents.com/events',
              to: '',
            },
          ].map((task, index) => {
            return (
              <Box
                key={task.text}
                sx={{
                  p: 0.6,
                  py: 0.3,
                  flex: 1,
                  width: '100%',
                  border: '1px solid',
                  borderColor: uicolors.blue[700],
                  backgroundColor:
                    uicolors.blue[900] + (task.completed ? '99' : '22'),
                  borderRadius: 2,
                }}
              >
                <Stack spacing={1} direction="row" alignItems="center">
                  <Box
                    sx={{
                      borderRadius: 50,
                      border: '1px solid',
                      borderColor: uicolors.blue[800],
                      backgroundColor: uicolors.black[700],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 22,
                      width: 22,
                    }}
                  >
                    {task.completed ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M10 18H8v-2h2v2Zm-2-2H6v-2h2v2Zm4-2v2h-2v-2h2Zm-6 0H4v-2h2v2Zm8 0h-2v-2h2v2Zm2-2h-2v-2h2v2Zm2-2h-2V8h2v2Zm2-2h-2V6h2v2Z" />
                      </svg>
                    ) : (
                      <Box sx={{ fontFamily: 'Racing Sans One' }}>
                        {index + 1}
                      </Box>
                    )}
                  </Box>
                  {task.href ? (
                    <Box
                      component="a"
                      href={task.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        flex: 1,
                        cursor: 'pointer',
                        color: uicolors.black[50],
                        textDecorationLine: 'none',
                        fontWeight: 'bold',
                        fontSize: { xs: 14, lg: 16 },
                      }}
                    >
                      {task.text}
                    </Box>
                  ) : task.to ? (
                    <Box
                      component={Link}
                      to={task.to}
                      sx={{
                        flex: 1,
                        cursor: 'pointer',
                        color: uicolors.black[50],
                        textDecorationLine: 'none',
                        fontWeight: 'bold',
                        fontSize: { xs: 14, lg: 16 },
                      }}
                    >
                      {task.text}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        flex: 1,
                        color: uicolors.black[50],
                        textDecorationLine: 'none',
                        fontWeight: 'bold',
                        fontSize: { xs: 14, lg: 16 },
                      }}
                    >
                      {task.text}
                    </Box>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Stack>

        <Stack spacing={1} direction="row" alignItems="center">
          <Box
            sx={{
              flex: 1,
              fontSize: 14,
              fontFamily: 'Lato',
              textTransform: 'uppercase',
              textAlign: 'center',
              letterSpacing: 1.1,
            }}
          >
            Fastract
          </Box>

          <Box sx={{ flex: 4 }}>
            <ProgressBar
              progress={queryProgress.data?.DirectDownlineCount || 0}
            />
          </Box>
        </Stack>

        <Stack spacing={1} direction="row" alignItems="center">
          <Box
            sx={{
              flex: 1,
              fontSize: 14,
              fontFamily: 'Lato',
              textTransform: 'uppercase',
              textAlign: 'center',
              letterSpacing: 1.1,
            }}
          >
            Enrolled
          </Box>

          <Box sx={{ flex: 4 }}>
            <ProgressBar progress={queryProgress.data?.XcelAgtCount || 0} />
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
}
